import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";
import api from "../lib/api";

const SPANS = [
  "md:col-span-7 aspect-[16/10]",
  "md:col-span-5 aspect-[4/3]",
  "md:col-span-5 aspect-[4/3]",
  "md:col-span-7 aspect-[16/10]",
  "md:col-span-6 aspect-[16/10]",
  "md:col-span-6 aspect-[16/10]",
];

export const Portfolio = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const close = useCallback(() => setOpenIdx(null), []);
  const step = useCallback(
    (d) => setOpenIdx((i) => (i === null ? i : (i + d + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (openIdx === null) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIdx, close, step]);

  return (
    <section id="portfolio" className="relative bg-[#0D0F12] px-6 py-28 lg:px-12 lg:py-40" data-testid="portfolio-section">
      <div className="mx-auto max-w-7xl">
        <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]">Portfolio</p>
            <h2 className="mt-4 font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl">
              {t("portfolio.h2pre")} <em className="italic text-[#D6705A]">{t("portfolio.h2em")}</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#A0A0A0]">
            {t("portfolio.intro")}
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-12">
          {items.map((item, i) => (
            <motion.figure
              key={item.id}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative col-span-1 cursor-pointer overflow-hidden ${SPANS[i % SPANS.length]}`}
              data-testid={`portfolio-item-${i}`}
              onClick={() => setOpenIdx(i)}
            >
              <img
                src={item.image_url}
                alt={`${item.title} — ${t("portfolio.imgAltSuffix")}`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/25 transition-opacity duration-500 group-hover:opacity-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0F12]/90 via-transparent to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
                <div>
                  <p className="font-code text-[10px] uppercase tracking-[0.3em] text-[#D6705A]">{item.category}</p>
                  <h3 className="mt-1 font-display text-2xl font-light text-[#F9F8F6] md:text-3xl">{item.title}</h3>
                </div>
                <span className="mb-1 hidden max-w-[200px] text-right text-xs leading-relaxed text-[#A0A0A0] opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:block">
                  {item.description}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {openIdx !== null && items[openIdx] && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0D0F12]/95 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            data-testid="lightbox-overlay"
          >
            <button
              onClick={close}
              className="absolute right-6 top-6 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-[#F9F8F6] transition-colors duration-300 hover:border-[#D6705A] hover:text-[#D6705A]"
              data-testid="lightbox-close"
              aria-label={t("portfolio.close")}
            >
              <X className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-[#F9F8F6] transition-colors duration-300 hover:border-[#D6705A] hover:text-[#D6705A] lg:left-10"
              data-testid="lightbox-prev"
              aria-label={t("portfolio.prev")}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-[#F9F8F6] transition-colors duration-300 hover:border-[#D6705A] hover:text-[#D6705A] lg:right-10"
              data-testid="lightbox-next"
              aria-label={t("portfolio.next")}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <motion.figure
              key={items[openIdx].id}
              className="px-16 text-center"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[openIdx].image_url}
                alt={`${items[openIdx].title} — ${t("portfolio.imgAltSuffix")}`}
                className="mx-auto max-h-[76vh] max-w-[88vw] object-contain"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-6">
                <p className="font-code text-[10px] uppercase tracking-[0.3em] text-[#D6705A]">{items[openIdx].category}</p>
                <h3 className="mt-1 font-display text-2xl font-light text-[#F9F8F6] md:text-3xl" data-testid="lightbox-title">
                  {items[openIdx].title}
                </h3>
                <p className="mt-2 font-code text-[10px] tracking-[0.3em] text-[#666]" data-testid="lightbox-counter">
                  {String(openIdx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                </p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
